import { ChevronDown, Key, Plus, UserPlus } from "lucide-react"
import * as React from "react"

import { Button } from "@/components/ui/button"
import { DataTable, Pagination, type Column } from "@/components/ui/data-table"
import { Dialog, DialogTrigger } from "@/components/ui/dialog"
import { EmptyState } from "@/components/ui/empty-state"
import { Kbd } from "@/components/ui/stat-card"
import { ActiveFilterChips } from "@/components/ui/table-filters"
import { type SortOption } from "@/components/ui/table-sort"
import { TableToolbar } from "@/components/ui/table-toolbar"
import { useToast } from "@/components/ui/toast"
import { FEATURES } from "@/lib/features"
import { useTable } from "@/lib/table"
import { useSlashShortcut } from "@/lib/use-slash-shortcut"
import { cn } from "@/lib/utils"

import {
  CreatedCell,
  KeyCell,
  LastSeenCell,
  LastUsedCell,
  RemoveUserCell,
  RevokeKeyCell,
  RoleCell,
  UserAccountCell,
} from "./users-keys-cells"
import {
  accountStatus,
  type ConsoleUser,
  type ServiceKey,
  labelOfRole,
} from "./users-keys-data"
import {
  AddUserDialog,
  CreateKeyDialog,
  RemoveUserDialog,
  RevealKeyDialog,
  RevokeKeyDialog,
} from "./users-keys-dialogs"
import { bannerFor } from "@/lib/api-error-message"

import { useQueryClient } from "@tanstack/react-query"

import { buildKeyFilterSchema, buildUserFilterSchema } from "./users-keys-filters"
import {
  useApiKeysQuery,
  useCreateUserMutation,
  useDeleteUserMutation,
  useResendInviteMutation,
  usersQuery,
} from "./users-keys-api"
import { filterKeys, filterUsers, queryKeys } from "./users-keys-data"
import { useUsersKeysStore } from "./users-keys-store"

const EMPTY_USERS: ConsoleUser[] = []
const EMPTY_KEYS: ServiceKey[] = []

const USER_SORT_OPTIONS: SortOption[] = [
  { id: "lastSeen", label: "Last seen", ascLabel: "Oldest first", descLabel: "Newest first" },
  { id: "email", label: "Email", ascLabel: "A → Z", descLabel: "Z → A" },
  { id: "role", label: "Role", ascLabel: "A → Z", descLabel: "Z → A" },
  { id: "createdAt", label: "Added", ascLabel: "Oldest first", descLabel: "Newest first" },
]

const KEY_SORT_OPTIONS: SortOption[] = [
  { id: "lastUsedAt", label: "Last used", ascLabel: "Oldest first", descLabel: "Newest first" },
  { id: "createdAt", label: "Created", ascLabel: "Oldest first", descLabel: "Newest first" },
  { id: "name", label: "Name", ascLabel: "A → Z", descLabel: "Z → A" },
]

export function UsersKeysPage(): JSX.Element {
  const { toast } = useToast()
  const userSearchRef = React.useRef<HTMLInputElement>(null)
  useSlashShortcut(userSearchRef)

  const [addUserOpen, setAddUserOpen] = React.useState(false)
  const [createKeyOpen, setCreateKeyOpen] = React.useState(false)
  const [pendingRemove, setPendingRemove] = React.useState<ConsoleUser | null>(null)
  const [pendingRevoke, setPendingRevoke] = React.useState<ServiceKey | null>(null)
  const [revealed, setRevealed] = React.useState<{ plaintext: string; name: string } | null>(null)

  const userSchema = React.useMemo(() => buildUserFilterSchema(), [])
  const keySchema = React.useMemo(() => buildKeyFilterSchema(), [])

  // Users list is server-paginated + searchable + sortable via /users.
  // Keys list is client-paginated over a single small fetch (backend endpoint is
  // not paginated yet; the seed produces at most a handful of rows).
  const qcTop = useQueryClient()
  const { data: apiKeys = EMPTY_KEYS, error: loadError } = useApiKeysQuery(qcTop)

  const store = useUsersKeysStore(EMPTY_USERS, apiKeys)

  const createUserMut = useCreateUserMutation()
  const deleteUserMut = useDeleteUserMutation()
  const resendInviteMut = useResendInviteMutation()

  const qc = useQueryClient()
  const fetchUsers = React.useCallback(
    async (params: {
      page: number
      pageSize: number
      sort: { field: string; dir: "asc" | "desc" } | null
      filters: Record<string, unknown>
      search: string
    }) => {
      const rawRole = params.filters["role"]
      const roleFilter = typeof rawRole === "string" ? rawRole : undefined
      const page = await qc.fetchQuery(
        usersQuery({
          page: params.page,
          pageSize: params.pageSize,
          search: params.search,
          sort: params.sort,
          role: roleFilter,
        }),
      )
      return { rows: page.items, total: page.total }
    },
    [qc],
  )
  const fetchKeys = React.useCallback(
    (params: Parameters<typeof queryKeys>[1]) =>
      Promise.resolve(queryKeys(store.keys, params)),
    [store.keys],
  )

  const usersTable = useTable<ConsoleUser>({
    fetchData: fetchUsers,
    defaultSort: { field: "lastSeen", dir: "desc" },
    storageKey: "users",
  })
  const keysTable = useTable<ServiceKey>({
    fetchData: fetchKeys,
    defaultSort: { field: "lastUsedAt", dir: "desc" },
    storageKey: "service-keys",
  })

  // Client-side axes that don't have server support yet (activity: signed-in/never).
  const filteredUserRows = React.useMemo(
    () => filterUsers(usersTable.state.rows, usersTable.state.filters as never),
    [usersTable.state.rows, usersTable.state.filters],
  )
  const filteredKeyRows = React.useMemo(
    () => filterKeys(keysTable.state.rows, keysTable.state.filters as never),
    [keysTable.state.rows, keysTable.state.filters],
  )

  const userColumns = React.useMemo<Array<Column<ConsoleUser>>>(
    () => [
      {
        id: "account",
        header: "Account",
        width: "minmax(0,1.6fr)",
        cell: (u) => <UserAccountCell user={u} />,
      },
      {
        id: "role",
        header: "Role",
        width: "160px",
        cell: (u) => (
          <RoleCell
            user={u}
            disabled={u.isBootstrap}
            onChange={(role) => {
              const previous = u.role
              store.changeRole(u.id, role)
              toast({
                tone: "success",
                title: `${u.email} is now ${labelOfRole(role)}`,
                action: { label: "Undo", onAction: () => store.changeRole(u.id, previous) },
              })
            }}
          />
        ),
      },
      {
        id: "lastSeen",
        header: "Last seen",
        width: "128px",
        priority: 2,
        cell: (u) => <LastSeenCell iso={u.lastSeen} />,
      },
      {
        id: "resend",
        header: "",
        width: "120px",
        align: "end",
        cell: (u) =>
          accountStatus(u) === "pending-invite" ? (
            <Button
              size="sm"
              variant="ghost"
              disabled={resendInviteMut.isPending}
              onClick={() =>
                resendInviteMut.mutate(u.id, {
                  onSuccess: () => {
                    usersTable.actions.refresh()
                    toast({ tone: "success", title: `Invitation resent to ${u.email}` })
                  },
                  onError: (err) => {
                    const banner = bannerFor(err, "the invitation")
                    toast({ tone: "error", title: banner.title, body: banner.body })
                  },
                })
              }
            >
              Resend invite
            </Button>
          ) : null,
      },
      {
        id: "remove",
        header: "",
        width: "56px",
        align: "end",
        cell: (u) => <RemoveUserCell user={u} onRemove={() => setPendingRemove(u)} />,
      },
    ],
    [resendInviteMut, store, toast],
  )

  const keyColumns = React.useMemo<Array<Column<ServiceKey>>>(
    () => [
      {
        id: "key",
        header: "Key",
        width: "minmax(0,1.4fr)",
        cell: (k) => <KeyCell item={k} />,
      },
      {
        id: "created",
        header: "Created",
        width: "160px",
        priority: 3,
        cell: (k) => <CreatedCell item={k} />,
      },
      {
        id: "lastUsed",
        header: "Last used",
        width: "140px",
        priority: 2,
        cell: (k) => <LastUsedCell iso={k.lastUsedAt} />,
      },
      {
        id: "revoke",
        header: "",
        width: "56px",
        align: "end",
        cell: (k) => <RevokeKeyCell item={k} onRevoke={() => setPendingRevoke(k)} />,
      },
    ],
    [],
  )

  const usersFiltered = usersTable.state.search.length > 0 || usersTable.state.filterCount > 0
  const keysFiltered = keysTable.state.search.length > 0 || keysTable.state.filterCount > 0

  return (
    <div className="flex flex-col gap-6 px-6 py-5">
      <header className="min-w-0">
        <h1 className="text-subtitle text-text">Users &amp; keys</h1>
      </header>

      {loadError && (() => {
        const banner = bannerFor(loadError, "users or API keys")
        return (
          <div
            role="alert"
            className="rounded-md border border-people/30 bg-people-tint/40 px-4 py-3 text-body-sm text-people"
          >
            <div className="font-medium">{banner.title}</div>
            <div className="mt-0.5">{banner.body}</div>
          </div>
        )
      })()}

      <SectionCard
        icon={<UserPlus className="size-4" aria-hidden="true" />}
        title="Users"
        subtitle="People who sign in to the console."
        count={store.users.length}
        action={
          <Dialog open={addUserOpen} onOpenChange={setAddUserOpen}>
            <DialogTrigger asChild>
              <Button variant="primary" size="sm" leadingIcon={<Plus />}>
                Add user
              </Button>
            </DialogTrigger>
            <AddUserDialog
              isSubmitting={createUserMut.isPending}
              onSubmit={(input) => {
                createUserMut.mutate(
                  {
                    email: input.email,
                    fullName: input.name,
                    role: input.role,
                    mode:
                      input.kind === "invite"
                        ? { kind: "invite" }
                        : { kind: "password", password: input.password },
                  },
                  {
                    onSuccess: () => {
                      setAddUserOpen(false)
                      usersTable.actions.refresh()
                      toast({
                        tone: "success",
                        title:
                          input.kind === "invite"
                            ? `Invitation sent to ${input.email}`
                            : `${input.email} added`,
                      })
                    },
                    onError: (err) => {
                      const banner = bannerFor(err, "the invitation")
                      toast({ tone: "error", title: banner.title, body: banner.body })
                    },
                  },
                )
              }}
            />
          </Dialog>
        }
      >
        <div className="border-t border-border-soft px-5 py-3">
          <TableToolbar
            search={usersTable.state.search}
            onSearchChange={usersTable.actions.setSearch}
            searchRef={userSearchRef}
            searchPlaceholder="Search by email, name or role"
            searchAriaLabel="Search users"
            searchSuffix={usersTable.state.search.length === 0 ? <Kbd keys="/" /> : null}
            filterSchema={userSchema}
            filterValues={usersTable.state.filters}
            onFiltersChange={usersTable.actions.setFilters}
            onClearAllFilters={usersTable.actions.clearAllFilters}
            sortOptions={USER_SORT_OPTIONS}
            sort={usersTable.state.sort}
            onSortChange={usersTable.actions.setSort}
          />
          <div className="mt-3">
            <ActiveFilterChips
              schema={userSchema}
              values={usersTable.state.filters}
              onRemove={usersTable.actions.clearFilter}
              onClearAll={usersTable.actions.clearAllFilters}
            />
          </div>
        </div>

        <div className="max-h-[calc(100dvh-360px)] overflow-auto border-t border-border-soft">
          <DataTable<ConsoleUser>
            label="Console users"
            columns={userColumns}
            rows={filteredUserRows}
            rowKey={(u) => u.id}
            className="rounded-none border-0"
            stickyHeader
            loading={usersTable.state.isLoading}
            empty={
              <EmptyState
                bare
                reason={usersFiltered ? "filtered" : "empty"}
                title={usersFiltered ? "Nothing matches" : "No users yet"}
                body={
                  usersFiltered
                    ? "Try clearing the search or filters."
                    : "Add someone to give them console access."
                }
                action={
                  usersFiltered ? (
                    <Button
                      size="sm"
                      onClick={() => {
                        usersTable.actions.setSearch("")
                        usersTable.actions.clearAllFilters()
                      }}
                    >
                      Clear filters
                    </Button>
                  ) : null
                }
              />
            }
          />
        </div>

        <div className="border-t border-border-soft">
          <Pagination
            page={usersTable.state.page}
            pageSize={usersTable.state.pageSize}
            total={usersTable.state.total}
            onPageChange={usersTable.actions.setPage}
            onPageSizeChange={usersTable.actions.setPageSize}
            pageSizes={[10, 25, 50]}
          />
        </div>
      </SectionCard>

      {/* The section is hidden rather than emptied: a key list that cannot be
          added to reads as a broken feature, not an absent one. */}
      {FEATURES.serviceApiKeys && (
        <SectionCard
          icon={<Key className="size-4" aria-hidden="true" />}
          title="Service API keys"
          subtitle="Long-lived credentials machines use to call the API."
          count={store.keys.length}
          collapsible
          defaultOpen={false}
          action={
            <Dialog open={createKeyOpen} onOpenChange={setCreateKeyOpen}>
              <DialogTrigger asChild>
                <Button variant="primary" size="sm" leadingIcon={<Key />}>
                  Create key
                </Button>
              </DialogTrigger>
              <CreateKeyDialog
                onSubmit={(name) => {
                  const { plaintext } = store.createKey(name)
                  setCreateKeyOpen(false)
                  setRevealed({ plaintext, name })
                }}
              />
            </Dialog>
          }
        >
          <div className="border-t border-border-soft px-5 py-3">
            <TableToolbar
              search={keysTable.state.search}
              onSearchChange={keysTable.actions.setSearch}
              searchPlaceholder="Search by name, prefix or author"
              searchAriaLabel="Search service keys"
              filterSchema={keySchema}
              filterValues={keysTable.state.filters}
              onFiltersChange={keysTable.actions.setFilters}
              onClearAllFilters={keysTable.actions.clearAllFilters}
              sortOptions={KEY_SORT_OPTIONS}
              sort={keysTable.state.sort}
              onSortChange={keysTable.actions.setSort}
            />
            <div className="mt-3">
              <ActiveFilterChips
                schema={keySchema}
                values={keysTable.state.filters}
                onRemove={keysTable.actions.clearFilter}
                onClearAll={keysTable.actions.clearAllFilters}
              />
            </div>
          </div>

          <div className="max-h-[calc(100dvh-460px)] overflow-auto border-t border-border-soft">
            <DataTable<ServiceKey>
              label="Service API keys"
              columns={keyColumns}
              rows={filteredKeyRows}
              rowKey={(k) => k.id}
              className="rounded-none border-0"
              stickyHeader
              loading={keysTable.state.isLoading}
              empty={
                <EmptyState
                  bare
                  reason={keysFiltered ? "filtered" : "empty"}
                  title={keysFiltered ? "Nothing matches" : "No service keys yet"}
                  body={
                    keysFiltered
                      ? "Try clearing the search or filters."
                      : "Create one to let a machine call the API."
                  }
                  action={
                    keysFiltered ? (
                      <Button
                        size="sm"
                        onClick={() => {
                          keysTable.actions.setSearch("")
                          keysTable.actions.clearAllFilters()
                        }}
                      >
                        Clear filters
                      </Button>
                    ) : null
                  }
                />
              }
            />
          </div>

          <div className="border-t border-border-soft px-5 py-3 text-body-sm text-text-3">
            Keys marked <span className="text-text-2">from env</span> are seeded by{" "}
            <code className="font-id text-caption text-text">PYXIS_API_KEYS</code> at boot and cannot
            be revoked here — remove them from the environment and restart.
          </div>

          <div className="border-t border-border-soft">
            <Pagination
              page={keysTable.state.page}
              pageSize={keysTable.state.pageSize}
              total={keysTable.state.total}
              onPageChange={keysTable.actions.setPage}
              onPageSizeChange={keysTable.actions.setPageSize}
              pageSizes={[10, 25, 50]}
            />
          </div>
        </SectionCard>
      )}

      <Dialog
        open={pendingRemove !== null}
        onOpenChange={(open) => !open && setPendingRemove(null)}
      >
        {pendingRemove && (
          <RemoveUserDialog
            email={pendingRemove.email}
            onConfirm={() => {
              const removed = pendingRemove
              deleteUserMut.mutate(removed.id, {
                onSuccess: () => {
                  setPendingRemove(null)
                  usersTable.actions.refresh()
                  toast({ tone: "success", title: `${removed.email} removed` })
                },
                onError: (err) => {
                  const banner = bannerFor(err, "the removal")
                  toast({ tone: "error", title: banner.title, body: banner.body })
                },
              })
            }}
          />
        )}
      </Dialog>

      <Dialog
        open={pendingRevoke !== null}
        onOpenChange={(open) => !open && setPendingRevoke(null)}
      >
        {pendingRevoke && (
          <RevokeKeyDialog
            serviceKey={pendingRevoke}
            onConfirm={() => {
              const revoked = pendingRevoke
              store.revokeKey(revoked.id)
              setPendingRevoke(null)
              toast({ tone: "success", title: `${revoked.name} revoked` })
            }}
          />
        )}
      </Dialog>

      <Dialog open={revealed !== null} onOpenChange={() => undefined}>
        {revealed && (
          <RevealKeyDialog
            plaintext={revealed.plaintext}
            keyName={revealed.name}
            onDone={() => setRevealed(null)}
          />
        )}
      </Dialog>
    </div>
  )
}

function SectionCard({
  icon,
  title,
  subtitle,
  count,
  action,
  children,
  collapsible = false,
  defaultOpen = true,
}: {
  icon: React.ReactNode
  title: string
  subtitle: string
  count: number
  action?: React.ReactNode
  children: React.ReactNode
  collapsible?: boolean
  defaultOpen?: boolean
}): JSX.Element {
  const [open, setOpen] = React.useState(defaultOpen)
  const bodyId = React.useId()
  const isOpen = collapsible ? open : true

  return (
    <section className="overflow-hidden rounded-xl border border-border bg-surface">
      <header className="flex flex-wrap items-center gap-3 bg-surface-sunken/50 px-5 py-3">
        <div className="flex min-w-0 flex-1 items-center gap-2.5">
          {collapsible ? (
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={isOpen}
              aria-controls={bodyId}
              className="-ml-1 flex min-w-0 flex-1 items-center gap-2.5 rounded-md px-1 py-0.5 text-left transition-colors duration-120 ease-out hover:bg-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--brand)]"
            >
              <ChevronDown
                className={cn(
                  "size-4 shrink-0 text-text-3 transition-transform duration-180 ease-out",
                  !isOpen && "-rotate-90",
                )}
                aria-hidden="true"
              />
              <SectionIdentity icon={icon} title={title} count={count} subtitle={subtitle} />
            </button>
          ) : (
            <SectionIdentity icon={icon} title={title} count={count} subtitle={subtitle} />
          )}
        </div>
        {action && isOpen && <div className="ml-auto shrink-0">{action}</div>}
      </header>
      {isOpen && (
        <div id={bodyId}>
          {children}
        </div>
      )}
    </section>
  )
}

function SectionIdentity({
  icon,
  title,
  count,
  subtitle,
}: {
  icon: React.ReactNode
  title: string
  count: number
  subtitle: string
}): JSX.Element {
  return (
    <>
      <span className="grid size-7 shrink-0 place-items-center rounded-md bg-surface text-text-2 ring-1 ring-inset ring-border">
        {icon}
      </span>
      <div className="min-w-0">
        <div className="flex items-baseline gap-2">
          <h2 className="text-heading text-text">{title}</h2>
          <span className="font-id tabular-nums text-caption text-text-3">{count}</span>
        </div>
        <p className="text-caption text-text-3">{subtitle}</p>
      </div>
    </>
  )
}
